import {
  type CommonZoneLayout,
  type DoorWall,
  MAP_COLS,
  MAP_FURNITURE_KINDS,
  MAP_ROWS,
  type MapFurniture,
  type MapLayoutDocument,
  type MapTransition,
  makePodFromBounds,
  type PersonalOfficeSlotLayout,
} from "@/lib/office/personal-office-layout";

const DOOR_WALLS: readonly DoorWall[] = ["N", "S", "E", "W"];
// Keep in sync with saveMapLayoutDraftSchema limits (campus is furniture-heavy).
const MAX_OFFICES = 50;
const MAX_ZONES = 40;
const MAX_FURNITURE = 400;
const MAX_SPAWNS = 40;
const MAX_TRANSITIONS = 40;

/** Clickable target in the map editor (mirrors EditorItemRef). */
export type MapLayoutIssueSelection =
  | { kind: "office"; id: string }
  | { kind: "zone"; id: string }
  | { kind: "furniture"; id: string }
  | { kind: "spawn"; index: number }
  | { kind: "transition"; id: string };

export type MapLayoutValidationIssue = {
  /** Machine path for debug / API compatibility (e.g. offices[2]). */
  path: string;
  /** Human-readable explanation (entity names, not only indices). */
  message: string;
  /** When set, the editor can jump to / highlight this object. */
  selection?: MapLayoutIssueSelection;
};

export type MapLayoutValidationResult =
  | { ok: true; layout: MapLayoutDocument }
  | { ok: false; issues: MapLayoutValidationIssue[] };

/**
 * True when rects share interior area. Shared edges (one wall tile between
 * adjacent offices) are allowed — `a.x2 === b.x1` is not an overlap.
 */
function rectsOverlap(
  a: { x1: number; y1: number; x2: number; y2: number },
  b: { x1: number; y1: number; x2: number; y2: number },
): boolean {
  return !(a.x2 <= b.x1 || b.x2 <= a.x1 || a.y2 <= b.y1 || b.y2 <= a.y1);
}

function inMap(gx: number, gy: number, cols: number, rows: number): boolean {
  return gx >= 0 && gy >= 0 && gx < cols && gy < rows;
}

function isDoorWall(v: unknown): v is DoorWall {
  return typeof v === "string" && (DOOR_WALLS as readonly string[]).includes(v);
}

function isFurnitureKind(v: unknown): v is MapFurniture["kind"] {
  return typeof v === "string" && (MAP_FURNITURE_KINDS as readonly string[]).includes(v);
}

function quote(label: string): string {
  return `"${label}"`;
}

function officeLabel(name: string, slotKey: string): string {
  const n = name.trim();
  if (n) return `Office ${quote(n)}`;
  return `Office ${quote(slotKey)}`;
}

function zoneLabel(name: string, id: string): string {
  const n = name.trim();
  if (n) return `Room ${quote(n)}`;
  return `Room ${quote(id)}`;
}

function furnitureLabel(kind: string, id: string, displayName?: string): string {
  const n = displayName?.trim();
  if (n) return `${kind} ${quote(n)}`;
  const short = kind.replaceAll("_", " ");
  return `${short} (${id.slice(0, 12)}${id.length > 12 ? "…" : ""})`;
}

function pushIssue(
  issues: MapLayoutValidationIssue[],
  path: string,
  message: string,
  selection?: MapLayoutIssueSelection,
): void {
  issues.push(selection ? { path, message, selection } : { path, message });
}

type Occupied = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  path: string;
  label: string;
  selection?: MapLayoutIssueSelection;
};

/**
 * Validate and normalize a map layout document.
 * Re-derives office furniture anchors from bounds + doorWall (editor may send stale anchors).
 */
export function validateMapLayout(raw: unknown): MapLayoutValidationResult {
  const issues: MapLayoutValidationIssue[] = [];
  if (!raw || typeof raw !== "object") {
    return { ok: false, issues: [{ path: "", message: "Layout must be an object" }] };
  }
  const doc = raw as Record<string, unknown>;

  const cols = typeof doc.cols === "number" ? doc.cols : MAP_COLS;
  const rows = typeof doc.rows === "number" ? doc.rows : MAP_ROWS;
  if (cols !== MAP_COLS || rows !== MAP_ROWS) {
    pushIssue(issues, "cols", `Map size is fixed at ${MAP_COLS}×${MAP_ROWS} for now`);
  }

  if (!Array.isArray(doc.offices)) {
    pushIssue(issues, "offices", "Offices list is missing or invalid");
    return { ok: false, issues };
  }
  if (!Array.isArray(doc.commonZones)) {
    pushIssue(issues, "commonZones", "Rooms / zones list is missing or invalid");
    return { ok: false, issues };
  }
  if (!Array.isArray(doc.furniture)) {
    pushIssue(issues, "furniture", "Furniture list is missing or invalid");
    return { ok: false, issues };
  }
  if (!Array.isArray(doc.spawnPoints)) {
    pushIssue(issues, "spawnPoints", "Spawn points list is missing or invalid");
    return { ok: false, issues };
  }
  // transitions optional for backward compat — normalize to [] when missing
  if (doc.transitions !== undefined && !Array.isArray(doc.transitions)) {
    pushIssue(issues, "transitions", "Transitions list is invalid");
    return { ok: false, issues };
  }

  // Default id for older documents without an explicit map id.
  const mapIdRaw = typeof doc.id === "string" ? doc.id.trim() : "";
  const mapId = mapIdRaw || "hq";
  if (!/^[a-z][a-z0-9_-]*$/.test(mapId) || mapId.length > 32) {
    pushIssue(issues, "id", "Map id must be snake_case or kebab-case (max 32 characters)");
  }

  if (doc.offices.length > MAX_OFFICES) {
    pushIssue(issues, "offices", `Too many offices (max ${MAX_OFFICES})`);
  }
  if (doc.commonZones.length > MAX_ZONES) {
    pushIssue(issues, "commonZones", `Too many rooms / zones (max ${MAX_ZONES})`);
  }
  if (doc.furniture.length > MAX_FURNITURE) {
    pushIssue(issues, "furniture", `Too many furniture pieces (max ${MAX_FURNITURE})`);
  }
  if (doc.spawnPoints.length > MAX_SPAWNS) {
    pushIssue(issues, "spawnPoints", `Too many spawn points (max ${MAX_SPAWNS})`);
  }
  if (doc.spawnPoints.length < 1) {
    pushIssue(issues, "spawnPoints", "Add at least one spawn point so players can join this map");
  }
  const transitionsRaw = Array.isArray(doc.transitions) ? doc.transitions : [];
  if (transitionsRaw.length > MAX_TRANSITIONS) {
    pushIssue(issues, "transitions", `Too many transitions (max ${MAX_TRANSITIONS})`);
  }

  const offices: PersonalOfficeSlotLayout[] = [];
  const officeKeys = new Set<string>();
  const occupied: Occupied[] = [];

  doc.offices.forEach((item, i) => {
    const path = `offices[${i}]`;
    if (!item || typeof item !== "object") {
      pushIssue(issues, path, `Office #${i + 1} is invalid`);
      return;
    }
    const o = item as Record<string, unknown>;
    const slotKey = typeof o.slotKey === "string" ? o.slotKey.trim() : "";
    const nameDefault = typeof o.nameDefault === "string" ? o.nameDefault.trim() : "";
    const x1 = Number(o.x1);
    const y1 = Number(o.y1);
    const x2 = Number(o.x2);
    const y2 = Number(o.y2);
    const doorWall = o.doorWall;
    const label = officeLabel(nameDefault, slotKey || `index ${i + 1}`);
    const selection: MapLayoutIssueSelection | undefined = slotKey
      ? { kind: "office", id: slotKey }
      : undefined;

    if (!slotKey || !/^office_[a-zA-Z0-9_-]+$/.test(slotKey)) {
      pushIssue(
        issues,
        `${path}.slotKey`,
        `${label}: slot key must look like office_1 (got ${quote(slotKey || "(empty)")})`,
        selection,
      );
      return;
    }
    if (officeKeys.has(slotKey)) {
      pushIssue(
        issues,
        `${path}.slotKey`,
        `${label}: duplicate slot key ${quote(slotKey)} — each office needs a unique key`,
        selection,
      );
      return;
    }
    officeKeys.add(slotKey);
    if (!nameDefault || nameDefault.length > 64) {
      pushIssue(
        issues,
        `${path}.nameDefault`,
        `${label}: name is required (max 64 characters)`,
        selection,
      );
    }
    if (
      !Number.isInteger(x1) ||
      !Number.isInteger(y1) ||
      !Number.isInteger(x2) ||
      !Number.isInteger(y2)
    ) {
      pushIssue(issues, path, `${label}: bounds must be whole numbers`, selection);
      return;
    }
    if (x2 - x1 < 4 || y2 - y1 < 4) {
      pushIssue(
        issues,
        path,
        `${label}: too small — offices must be at least 5×5 tiles (inclusive)`,
        selection,
      );
      return;
    }
    if (x1 < 1 || y1 < 1 || x2 >= cols - 1 || y2 >= rows - 1) {
      pushIssue(
        issues,
        path,
        `${label}: must sit inside the outer map shell (leave a 1-tile border)`,
        selection,
      );
      return;
    }
    if (!isDoorWall(doorWall)) {
      pushIssue(issues, `${path}.doorWall`, `${label}: door wall must be N, S, E, or W`, selection);
      return;
    }

    const pod = makePodFromBounds({
      slotKey,
      nameDefault: nameDefault || slotKey,
      x1,
      y1,
      x2,
      y2,
      doorWall,
    });
    for (const other of occupied) {
      if (rectsOverlap(pod, other)) {
        pushIssue(issues, path, `${label} overlaps ${other.label}`, selection);
      }
    }
    occupied.push({
      x1: pod.x1,
      y1: pod.y1,
      x2: pod.x2,
      y2: pod.y2,
      path,
      label,
      selection,
    });
    offices.push(pod);
  });

  const commonZones: CommonZoneLayout[] = [];
  const zoneIds = new Set<string>();

  doc.commonZones.forEach((item, i) => {
    const path = `commonZones[${i}]`;
    if (!item || typeof item !== "object") {
      pushIssue(issues, path, `Room / zone #${i + 1} is invalid`);
      return;
    }
    const z = item as Record<string, unknown>;
    const id = typeof z.id === "string" ? z.id.trim() : "";
    const name = typeof z.name === "string" ? z.name.trim() : "";
    const x1 = Number(z.x1);
    const y1 = Number(z.y1);
    const x2 = Number(z.x2);
    const y2 = Number(z.y2);
    const rug = typeof z.rug === "string" ? z.rug : "#EAF1F9";
    const priv = z.priv === true;
    const roomKind = z.roomKind === "meeting" || z.roomKind === "huddle" ? z.roomKind : undefined;
    const label = zoneLabel(name, id || `index ${i + 1}`);
    const selection: MapLayoutIssueSelection | undefined = id ? { kind: "zone", id } : undefined;

    if (!id || !/^[a-z][a-z0-9_]*$/.test(id)) {
      pushIssue(
        issues,
        `${path}.id`,
        `${label}: id must be snake_case (letters, numbers, underscores)`,
        selection,
      );
      return;
    }
    if (zoneIds.has(id) || officeKeys.has(id)) {
      pushIssue(issues, `${path}.id`, `${label}: duplicate id ${quote(id)}`, selection);
      return;
    }
    zoneIds.add(id);
    if (!name || name.length > 64) {
      pushIssue(
        issues,
        `${path}.name`,
        `${label}: name is required (max 64 characters)`,
        selection,
      );
    }
    if (
      !Number.isInteger(x1) ||
      !Number.isInteger(y1) ||
      !Number.isInteger(x2) ||
      !Number.isInteger(y2)
    ) {
      pushIssue(issues, path, `${label}: bounds must be whole numbers`, selection);
      return;
    }
    if (x2 < x1 || y2 < y1) {
      pushIssue(
        issues,
        path,
        `${label}: invalid bounds — bottom-right must be ≥ top-left`,
        selection,
      );
      return;
    }
    const minSpan = roomKind === "meeting" ? 5 : roomKind === "huddle" ? 3 : 2;
    if (x2 - x1 < minSpan || y2 - y1 < minSpan) {
      const typeHint = roomKind ? ` for a ${roomKind} room` : "";
      pushIssue(
        issues,
        path,
        `${label}: too small${typeHint} — needs at least ${minSpan + 1}×${minSpan + 1} tiles`,
        selection,
      );
      return;
    }
    if (x1 < 1 || y1 < 1 || x2 >= cols - 1 || y2 >= rows - 1) {
      pushIssue(
        issues,
        path,
        `${label}: must sit inside the outer map shell (leave a 1-tile border)`,
        selection,
      );
      return;
    }

    const zone: CommonZoneLayout = { id, name: name || id, x1, y1, x2, y2, rug, priv, roomKind };
    for (const other of occupied) {
      if (rectsOverlap(zone, other)) {
        pushIssue(issues, path, `${label} overlaps ${other.label}`, selection);
      }
    }
    occupied.push({ x1, y1, x2, y2, path, label, selection });
    commonZones.push(zone);
  });

  const furniture: MapFurniture[] = [];
  const furnIds = new Set<string>();
  doc.furniture.forEach((item, i) => {
    const path = `furniture[${i}]`;
    if (!item || typeof item !== "object") {
      pushIssue(issues, path, `Furniture #${i + 1} is invalid`);
      return;
    }
    const f = item as Record<string, unknown>;
    const id = typeof f.id === "string" ? f.id.trim() : "";
    const kind = f.kind;
    const gx = Number(f.gx);
    const gy = Number(f.gy);
    const w = Number(f.w ?? 1);
    const d = Number(f.d ?? 1);
    const dir = isDoorWall(f.dir) ? f.dir : "S";
    const displayName = typeof f.displayName === "string" ? f.displayName : undefined;
    const kindStr = typeof kind === "string" ? kind : "furniture";
    const label = furnitureLabel(kindStr, id || `index ${i + 1}`, displayName);
    const selection: MapLayoutIssueSelection | undefined = id
      ? { kind: "furniture", id }
      : undefined;

    if (!id) {
      pushIssue(issues, `${path}.id`, `${label}: missing id`, selection);
      return;
    }
    if (furnIds.has(id)) {
      pushIssue(issues, `${path}.id`, `Duplicate furniture id ${quote(id)}`, selection);
      return;
    }
    furnIds.add(id);
    if (!isFurnitureKind(kind)) {
      pushIssue(
        issues,
        `${path}.kind`,
        `${label}: unknown furniture type ${quote(String(kind))}`,
        selection,
      );
      return;
    }
    // Campus building shells use multi-tile wall masses (up to ~14×12).
    if (!Number.isInteger(w) || !Number.isInteger(d) || w < 1 || d < 1 || w > 16 || d > 16) {
      pushIssue(
        issues,
        path,
        `${label}: size must be whole numbers between 1 and 16 tiles`,
        selection,
      );
      return;
    }
    if (
      !Number.isInteger(gx) ||
      !Number.isInteger(gy) ||
      !inMap(gx, gy, cols, rows) ||
      !inMap(gx + w - 1, gy + d - 1, cols, rows)
    ) {
      pushIssue(
        issues,
        path,
        `${label} at (${gx}, ${gy}) size ${w}×${d} sits off the map — move it or shrink it`,
        selection,
      );
      return;
    }
    const piece: MapFurniture = {
      id,
      kind,
      gx,
      gy,
      w,
      d,
      dir,
      seat: f.seat === true,
      block: f.block === true,
    };
    // Optional draw props (campus building color / entrance labels / facade).
    if (typeof f.c === "string" && f.c.length <= 32) piece.c = f.c;
    if (typeof f.displayName === "string" && f.displayName.length <= 64) {
      piece.displayName = f.displayName;
    }
    if (isDoorWall(f.doorWall)) piece.doorWall = f.doorWall;
    if (typeof f.style === "string") {
      const style = f.style
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "");
      if (style.length > 0 && style.length <= 32) piece.style = style;
    }
    furniture.push(piece);
  });

  const spawnPoints: Array<{ gx: number; gy: number }> = [];
  doc.spawnPoints.forEach((item, i) => {
    const path = `spawnPoints[${i}]`;
    const selection: MapLayoutIssueSelection = { kind: "spawn", index: i };
    if (!item || typeof item !== "object") {
      pushIssue(issues, path, `Spawn #${i + 1} is invalid`, selection);
      return;
    }
    const p = item as Record<string, unknown>;
    const gx = Number(p.gx);
    const gy = Number(p.gy);
    if (!Number.isInteger(gx) || !Number.isInteger(gy) || !inMap(gx, gy, cols, rows)) {
      pushIssue(issues, path, `Spawn #${i + 1} at (${gx}, ${gy}) is off the map`, selection);
      return;
    }
    // Spawns must not sit inside personal offices (common rooms OK — default HQ
    // spawns historically sit on the meeting floor plate / commons aisle).
    const inOffice = offices.find((o) => gx >= o.x1 && gx <= o.x2 && gy >= o.y1 && gy <= o.y2);
    if (inOffice) {
      pushIssue(
        issues,
        path,
        `Spawn #${i + 1} is inside ${officeLabel(inOffice.nameDefault, inOffice.slotKey)} — move it to a hallway or common area`,
        selection,
      );
      return;
    }
    spawnPoints.push({ gx, gy });
  });

  const transitions: MapTransition[] = [];
  const transitionIds = new Set<string>();
  transitionsRaw.forEach((item, i) => {
    const path = `transitions[${i}]`;
    if (!item || typeof item !== "object") {
      pushIssue(issues, path, `Transition #${i + 1} is invalid`);
      return;
    }
    const t = item as Record<string, unknown>;
    const id = typeof t.id === "string" ? t.id.trim() : "";
    const gx = Number(t.gx);
    const gy = Number(t.gy);
    const toMapId = typeof t.toMapId === "string" ? t.toMapId.trim() : "";
    const label = typeof t.label === "string" ? t.label.trim().slice(0, 64) : undefined;
    const display = label ? `Transition ${quote(label)}` : `Transition #${i + 1}`;
    const selection: MapLayoutIssueSelection | undefined = id
      ? { kind: "transition", id }
      : undefined;
    const spawnRaw =
      t.toSpawn && typeof t.toSpawn === "object" ? (t.toSpawn as Record<string, unknown>) : null;
    const toGx = spawnRaw ? Number(spawnRaw.gx) : Number.NaN;
    const toGy = spawnRaw ? Number(spawnRaw.gy) : Number.NaN;

    if (!id || !/^[a-z][a-z0-9_-]*$/.test(id)) {
      pushIssue(issues, `${path}.id`, `${display}: id must be snake_case or kebab-case`, selection);
      return;
    }
    if (transitionIds.has(id)) {
      pushIssue(issues, `${path}.id`, `${display}: duplicate id ${quote(id)}`, selection);
      return;
    }
    transitionIds.add(id);
    if (!Number.isInteger(gx) || !Number.isInteger(gy) || !inMap(gx, gy, cols, rows)) {
      pushIssue(issues, path, `${display} tile at (${gx}, ${gy}) is off the map`, selection);
      return;
    }
    if (!toMapId || toMapId.length > 32) {
      pushIssue(issues, `${path}.toMapId`, `${display}: pick a destination map`, selection);
      return;
    }
    if (!Number.isInteger(toGx) || !Number.isInteger(toGy)) {
      pushIssue(
        issues,
        `${path}.toSpawn`,
        `${display}: destination spawn X/Y must be whole numbers`,
        selection,
      );
      return;
    }
    // Destination spawn is on another map — only require non-negative ints (not this map bounds).
    if (toGx < 0 || toGy < 0 || toGx > 255 || toGy > 255) {
      pushIssue(
        issues,
        `${path}.toSpawn`,
        `${display}: destination spawn (${toGx}, ${toGy}) is out of range`,
        selection,
      );
      return;
    }
    transitions.push({
      id,
      gx,
      gy,
      toMapId,
      toSpawn: { gx: toGx, gy: toGy },
      ...(label ? { label } : {}),
    });
  });

  if (issues.length > 0) return { ok: false, issues };

  return {
    ok: true,
    layout: {
      id: mapId,
      cols: MAP_COLS,
      rows: MAP_ROWS,
      offices,
      commonZones,
      furniture,
      spawnPoints,
      transitions,
    },
  };
}
